import { useState } from "react";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { ComparisonTable, type Row } from "../components/fiscal/ComparisonTable.js";
import { FiscalCard, FiscalShell, LoadingSkeleton } from "../components/fiscal/Shell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";

/**
 * F-recettes-encaissees Fase B — dépenses CAIXA do mês: as 9 caixas
 * sincronizadas do relevé/compras. Dotations (amortização/provisões) e IS
 * ficam de fora — não são desembolso bancário do mês.
 */
function cashExpenses(e: {
  fournituresConsommables: string;
  locationsChargesLocatives: string;
  primesAssurance: string;
  intermediairesHonoraires: string;
  deplacementsReception: string;
  fraisBancaires: string;
  impotsTaxes: string;
  chargesPersonnel: string;
  autresChargesExploitation: string;
}): number {
  return (
    Number(e.fournituresConsommables) +
    Number(e.locationsChargesLocatives) +
    Number(e.primesAssurance) +
    Number(e.intermediairesHonoraires) +
    Number(e.deplacementsReception) +
    Number(e.fraisBancaires) +
    Number(e.impotsTaxes) +
    Number(e.chargesPersonnel) +
    Number(e.autresChargesExploitation)
  );
}

export default function FiscalResultat() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  // Fase B — visão dupla: Engagement (faturas, motor fiscal) × Trésorerie
  // (caixa: recettes encaissées − dépenses do relevé, direto dos lançamentos).
  const [view, setView] = useState<"engagement" | "tresorerie">("engagement");

  const calcQuery = trpc.fiscalCalculations.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );
  const budgetQuery = trpc.fiscalBudgets.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null, retry: false },
  );
  const entriesQuery = trpc.fiscalMonthEntries.list.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null && view === "tresorerie" },
  );

  const calc = calcQuery.data;
  const budget = budgetQuery.data;

  const toolbar = (
    <FiscalYearSelector
      companyId={companyId}
      fiscalYearId={fiscalYearId}
      onChangeCompany={setCompanyId}
      onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
    />
  );

  if (companyId === null || fiscalYearId === null) {
    return (
      <FiscalShell subtitle={t("fiscalResultat.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalResultat.selectCompanyPrompt")}</p>
        </FiscalCard>
      </FiscalShell>
    );
  }

  const rows: Row[] = !calc
    ? []
    : [
        {
          label: t("fiscalResultat.rowCaHt"),
          realized: calc.caHt,
          annualized: calc.caHt,
          budget: budget?.caHt,
          emphasis: "subtotal",
        },
        {
          label: t("fiscalResultat.rowAchatsConsommes"),
          realized: calc.achatsConsommes,
          annualized: calc.achatsConsommes,
          budget: budget?.achatsConsommes,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowAutresChargesVariables"),
          realized: calc.autresChargesVariables,
          annualized: calc.autresChargesVariables,
          budget: budget?.autresChargesVariables,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowMargeGlobale"),
          realized: calc.margeGlobaleCumulative,
          annualized: calc.margeGlobaleAnnualized,
          budget: budget?.margeGlobaleProjected,
          emphasis: "subtotal",
          hint: t("fiscalResultat.hintMargeGlobale"),
        },
        {
          label: t("fiscalResultat.rowChargesExternesTotal"),
          realized: calc.chargesExternesTotal,
          annualized: calc.chargesExternesTotal,
          budget: budget?.chargesExternesTotalProjected,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowFournituresConsommables"),
          realized: calc.fournituresConsommables,
          annualized: calc.fournituresConsommables,
          budget: budget?.fournituresConsommables,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowLocationsChargesLocatives"),
          realized: calc.locationsChargesLocatives,
          annualized: calc.locationsChargesLocatives,
          budget: budget?.locationsChargesLocatives,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowPrimesAssurance"),
          realized: calc.primesAssurance,
          annualized: calc.primesAssurance,
          budget: budget?.primesAssurance,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowIntermediairesHonoraires"),
          realized: calc.intermediairesHonoraires,
          annualized: calc.intermediairesHonoraires,
          budget: budget?.intermediairesHonoraires,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowDeplacementsReception"),
          realized: calc.deplacementsReception,
          annualized: calc.deplacementsReception,
          budget: budget?.deplacementsReception,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowFraisBancaires"),
          realized: calc.fraisBancaires,
          annualized: calc.fraisBancaires,
          budget: budget?.fraisBancaires,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowValeurAjoutee"),
          realized: calc.vaCumulative,
          annualized: calc.vaAnnualized,
          budget: budget?.vaProjected,
          emphasis: "subtotal",
          hint: t("fiscalResultat.hintValeurAjoutee"),
        },
        {
          label: t("fiscalResultat.rowImpotsTaxes"),
          realized: calc.impotsTaxes,
          annualized: calc.impotsTaxes,
          budget: budget?.impotsTaxes,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowChargesPersonnel"),
          realized: calc.chargesPersonnel,
          annualized: calc.chargesPersonnel,
          budget: budget?.chargesPersonnel,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowSubventionsExploitation"),
          realized: calc.subventionsExploitation,
          annualized: calc.subventionsExploitation,
          budget: budget?.subventionsExploitation,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowEbe"),
          realized: calc.ebeCumulative,
          annualized: calc.ebeAnnualized,
          budget: budget?.ebeProjected,
          emphasis: "subtotal",
          hint: t("fiscalResultat.hintEbe"),
        },
        {
          label: t("fiscalResultat.rowDotationsAmortissements"),
          realized: calc.dotationsAmortissements,
          annualized: calc.dotationsAmortissements,
          budget: budget?.dotationsAmortissements,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowDotationsProvisions"),
          realized: calc.dotationsProvisions,
          annualized: calc.dotationsProvisions,
          budget: budget?.dotationsProvisions,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowAutresChargesExploitation"),
          realized: calc.autresChargesExploitation,
          annualized: calc.autresChargesExploitation,
          budget: budget?.autresChargesExploitation,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowAutresProduitsExploitation"),
          realized: calc.autresProduitsExploitation,
          annualized: calc.autresProduitsExploitation,
          budget: budget?.autresProduitsExploitation,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowResultatExploitation"),
          realized: calc.resultatExploitationCumulative,
          annualized: calc.resultatExploitationAnnualized,
          budget: budget?.resultatExploitationProjected,
          emphasis: "subtotal",
        },
        {
          label: t("fiscalResultat.rowImpotSocietes"),
          realized: calc.impotSocietes,
          annualized: calc.impotSocietes,
          budget: budget?.impotSocietes,
          indent: true,
        },
        {
          label: t("fiscalResultat.rowResultatNet"),
          realized: calc.resultatNetCumulative,
          annualized: calc.resultatNetAnnualized,
          budget: budget?.resultatNetProjected,
          emphasis: "total",
        },
        {
          label: t("fiscalResultat.rowCaf"),
          realized: calc.cafCumulative,
          annualized: calc.cafAnnualized,
          budget: budget?.cafProjected,
          emphasis: "total",
          hint: t("fiscalResultat.hintCaf"),
        },
      ];

  const entries = entriesQuery.data ?? [];
  // Só meses com movimento (recette ou dépense) entram na visão caixa.
  const cashRows = entries
    .map((e) => ({
      month: e.month,
      year: e.year,
      recettes: Number(e.recettesEncaisseesHt),
      tva: Number(e.tvaCollecteeEncaissements),
      aTrier: Number(e.recettesATrier),
      depenses: cashExpenses(e),
    }))
    .filter((r) => r.recettes !== 0 || r.depenses !== 0 || r.aTrier !== 0);
  let running = 0;
  const cashRowsCum = cashRows.map((r) => {
    const resultat = r.recettes - r.depenses;
    running += resultat;
    return { ...r, resultat, cumule: running };
  });
  const cashTotals = cashRows.reduce(
    (acc, r) => ({
      recettes: acc.recettes + r.recettes,
      tva: acc.tva + r.tva,
      aTrier: acc.aTrier + r.aTrier,
      depenses: acc.depenses + r.depenses,
    }),
    { recettes: 0, tva: 0, aTrier: 0, depenses: 0 },
  );

  const viewBtn = (v: "engagement" | "tresorerie", label: string) => (
    <button
      type="button"
      onClick={() => setView(v)}
      className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
        view === v
          ? "border-lime bg-lime/10 text-lime"
          : "border-neutral-700 text-neutral-300 hover:border-neutral-500"
      }`}
    >
      {label}
    </button>
  );

  return (
    <FiscalShell subtitle={t("fiscalResultat.subtitle")} toolbar={toolbar}>
      {/* Fase B — seletor de base: faturas (competência) × relevé (caixa). */}
      <div className="flex items-center gap-2">
        {viewBtn("engagement", t("fiscalResultat.viewEngagement"))}
        {viewBtn("tresorerie", t("fiscalResultat.viewTresorerie"))}
      </div>

      {view === "engagement" && calcQuery.isLoading && (
        <FiscalCard>
          <LoadingSkeleton rows={10} />
        </FiscalCard>
      )}
      {view === "engagement" && !calcQuery.isLoading && !calc && (
        <FiscalCard title={t("fiscalResultat.noKpisTitle")}>
          <p className="text-sm text-neutral-400">
            {t("fiscalResultat.runRecomputePre")}{" "}
            <strong>{t("fiscalResultat.recomputeKpisBtn")}</strong>{" "}
            {t("fiscalResultat.runRecomputeSuf")}
          </p>
        </FiscalCard>
      )}
      {view === "engagement" && calc && (
        <FiscalCard
          title={t("fiscalResultat.cascadeTitle")}
          subtitle={
            budget
              ? t("fiscalResultat.cardSubtitleWithBudget")
              : t("fiscalResultat.cardSubtitleNoBudget")
          }
          padded={false}
        >
          <ComparisonTable
            rows={rows}
            columns={{
              showAnnualized: true,
              showBudget: !!budget,
              showVariance: !!budget,
            }}
          />
        </FiscalCard>
      )}

      {view === "tresorerie" && entriesQuery.isLoading && (
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      )}
      {view === "tresorerie" && !entriesQuery.isLoading && (
        <FiscalCard
          title={t("fiscalResultat.tresoTitle")}
          subtitle={t("fiscalResultat.tresoSubtitle")}
          padded={false}
        >
          {cashRowsCum.length === 0 ? (
            <p className="p-4 text-sm text-neutral-400">{t("fiscalResultat.tresoEmpty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <tr>
                    <th className="text-left px-4 py-2">{t("fiscalResultat.tresoColMois")}</th>
                    <th className="text-right px-4 py-2">{t("fiscalResultat.tresoColRecettes")}</th>
                    <th className="text-right px-4 py-2">{t("fiscalResultat.tresoColTva")}</th>
                    <th className="text-right px-4 py-2">{t("fiscalResultat.tresoColDepenses")}</th>
                    <th className="text-right px-4 py-2">{t("fiscalResultat.tresoColResultat")}</th>
                    <th className="text-right px-4 py-2">{t("fiscalResultat.tresoColCumule")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-900">
                  {cashRowsCum.map((r) => (
                    <tr key={`${r.year}-${r.month}`}>
                      <td className="px-4 py-2 tabular-nums">
                        {String(r.month).padStart(2, "0")}/{r.year}
                        {r.aTrier > 0 && (
                          <span
                            className="ml-1.5 text-amber-400"
                            title={`${t("fiscalResultat.tresoATrierPre")} ${formatMoney(r.aTrier, { decimals: 2 })}`}
                          >
                            ⚠
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-lime">
                        {formatMoney(r.recettes, { decimals: 2 })}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-neutral-400">
                        {formatMoney(r.tva, { decimals: 2 })}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-red-300">
                        −{formatMoney(r.depenses, { decimals: 2 })}
                      </td>
                      <td
                        className={`px-4 py-2 text-right tabular-nums font-semibold ${
                          r.resultat >= 0 ? "text-lime" : "text-red-300"
                        }`}
                      >
                        {formatMoney(r.resultat, { decimals: 2 })}
                      </td>
                      <td
                        className={`px-4 py-2 text-right tabular-nums ${
                          r.cumule >= 0 ? "text-neutral-200" : "text-red-300"
                        }`}
                      >
                        {formatMoney(r.cumule, { decimals: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-neutral-800 text-sm font-semibold">
                  <tr>
                    <td className="px-4 py-2">
                      {t("fiscalResultat.tresoTotalLabel")}
                      {cashTotals.aTrier > 0 && (
                        <span className="ml-2 text-xs font-normal text-amber-400">
                          ⚠ {t("fiscalResultat.tresoATrierPre")}{" "}
                          {formatMoney(cashTotals.aTrier, { decimals: 2 })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-lime">
                      {formatMoney(cashTotals.recettes, { decimals: 2 })}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-neutral-400">
                      {formatMoney(cashTotals.tva, { decimals: 2 })}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-red-300">
                      −{formatMoney(cashTotals.depenses, { decimals: 2 })}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${
                        cashTotals.recettes - cashTotals.depenses >= 0
                          ? "text-lime"
                          : "text-red-300"
                      }`}
                      colSpan={2}
                    >
                      {formatMoney(cashTotals.recettes - cashTotals.depenses, { decimals: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="px-4 py-3 text-xs text-neutral-500 border-t border-neutral-900">
                {t("fiscalResultat.tresoNote")}
              </p>
            </div>
          )}
        </FiscalCard>
      )}
    </FiscalShell>
  );
}
